import React, { useEffect, useState } from 'react';
import { Button, Select, MenuItem, InputLabel, FormControl, LinearProgress, Box, Typography, ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import TableChartIcon from '@mui/icons-material/TableChart';
import WorldMap from "react-svg-worldmap";
import * as api_dash from '../../api/Dashboard.tsx';
import Legend, { LegendItem } from '../Legend.tsx';
import { useDependencies } from '../../misc/DependenciesProvider.tsx';
import type { CountryContext, DataItem } from "react-svg-worldmap";
import { DensityBin, ChartFilter } from '../../api/FracApi.ts';

export function DashboardDensityMap() {
    const { fracAPI } = useDependencies();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMoaCodes, setIsLoadingMoaCodes] = useState(false);
    const [moaCode, setMoaCode] = useState<string>('All');
    const [fracMoaCodes, setFracMoaCodes] = useState<string[]>([]);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [mapData, setMapData] = useState<DataItem<number>[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [viewMode, setViewMode] = useState<'map' | 'table'>('map');

    // State for dynamic buckets
    const [buckets, setBuckets] = useState<DensityBin[]>([]);
    const [legendData, setLegendData] = useState<LegendItem[]>([]);

    // Fetch MoA codes and bins whenever the year changes
    useEffect(() => {
        async function fetchYearData() {
            setIsLoadingMoaCodes(true);
            try {
                // Fetch MoA codes for this year
                const codes = await fracAPI.getMapMoaCodesByYear(selectedYear);
                setFracMoaCodes(codes);
                
                // Reset moaCode based on available codes
                if (codes.length === 0) {
                    setMoaCode('');  // No data available
                } else if (moaCode === '' || (moaCode !== 'All' && !codes.includes(moaCode))) {
                    setMoaCode('All');  // Reset to "All" if empty or current selection not available
                }
                
                // Fetch bins for this year
                const fetchedBins = await fracAPI.getMapDensityBins(selectedYear);
                setBuckets(fetchedBins);
                setLegendData(fetchedBins.map(b => ({ color: b.color, label: b.label })));
            } catch (e) {
                console.error("Error fetching year data", e);
            } finally {
                setIsLoadingMoaCodes(false);
            }
        }
        fetchYearData();
    }, [selectedYear, fracAPI]);

    // Dynamic color resolution based on state buckets
    const getColorForValue = (value: number | string | undefined) => {
        if (!buckets.length) return "#e0e0e0"; // fallback
        if (!value) return buckets[0].color;

        const numVal = Number(value);
        const bucket = buckets.find(b => numVal >= b.min_val && numVal <= b.max_val);
        return bucket ? bucket.color : buckets[0].color;
    };

    const getStyle = ({ countryValue }: CountryContext<string | number>) => ({
        fill: getColorForValue(countryValue)
    });

    const searchData = async () => {
        if (!moaCode) return;
        setIsLoading(true);
        setHasSearched(true);
        try {
            const filter = new ChartFilter({ moa_code: moaCode, years: [selectedYear] });
            const result = await fracAPI.getGlobalMapDensity(filter);
            const mappedData = Object.keys(result).map(key => ({ country: key, value: result[key] }));
            setMapData(mappedData);
        } catch (error) {
            console.error("Error fetching map density", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'map' | 'table' | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    const hasNoData = fracMoaCodes.length === 0 && !isLoadingMoaCodes;

    return (
        <div>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                <FormControl sx={{ width: '150px' }}>
                    <InputLabel id="year-label">Year</InputLabel>
                    <Select 
                        labelId="year-label"
                        value={selectedYear} 
                        label="Year" 
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {[...Array(10)].map((_, i) => <MenuItem key={i} value={2020 + i}>{2020 + i}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl disabled={hasNoData} sx={{ width: '300px' }}>
                    <InputLabel id="moa-code-label">FRAC MoA Code</InputLabel>
                    <Select 
                        labelId="moa-code-label"
                        value={moaCode} 
                        label="FRAC MoA Code" 
                        onChange={(e) => setMoaCode(e.target.value)}
                    >
                        {hasNoData ? (
                            <MenuItem value="">No data for year</MenuItem>
                        ) : [
                            <MenuItem key="all" value="All">All</MenuItem>,
                            ...fracMoaCodes.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)
                        ]}
                    </Select>
                </FormControl>
                <Button variant="contained" color="primary" onClick={searchData} disabled={!moaCode || isLoading || hasNoData}>Search</Button>
            </Box>

            {isLoading && <LinearProgress />}

            {/* Always show toggle and content area */}
            {!isLoading && (
                <>
                    <Box>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewModeChange}
                            size="small"
                        >
                            <ToggleButton value="map">
                                <MapIcon sx={{ mr: 1 }} />
                                Map
                            </ToggleButton>
                            <ToggleButton value="table">
                                <TableChartIcon sx={{ mr: 1 }} />
                                Table
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {hasSearched && mapData.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Found data for {mapData.length} {mapData.length === 1 ? 'country' : 'countries'}
                            </Typography>
                        )}
                    </Box>

                    {/* Show no data message only after search */}
                    {hasSearched && mapData.length === 0 ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No data found for {moaCode === 'All' ? 'any MoA code' : `MoA code "${moaCode}"`} in year {selectedYear}.
                        </Alert>
                    ) : (
                        <>
                            {viewMode === 'map' ? (
                                <div style={{ marginTop: '20px' }}>
                                    <WorldMap color="white" value-suffix="data points" size="xl" data={mapData} richInteraction styleFunction={getStyle} />
                                    <Legend items={legendData} />
                                </div>
                            ) : (
                                <>
                                    {mapData.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                            Click "Search" to load data
                                        </Typography>
                                    ) : (
                                        <TableContainer component={Paper} sx={{ mt: 2, maxWidth: 400 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><strong>Country Code</strong></TableCell>
                                                        <TableCell align="right"><strong>Data Points</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {mapData
                                                        .sort((a, b) => (b.value as number) - (a.value as number))
                                                        .map((row) => (
                                                            <TableRow key={row.country}>
                                                                <TableCell>{row.country}</TableCell>
                                                                <TableCell align="right">{row.value}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
